import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { LoadingSpinner } from "components/LoadingSpinner/";
import { useHasCompletedQuestionnaire } from "hooks/useHasCompletedQuestionnaire";
import { PageWrapper } from "./styles";
import { OnboardingHomeView } from "./OnboardingHomeView";
import { WelcomeModal } from "./WelcomeModal";

const WELCOME_SEEN_KEY = "libertify_ftu_welcome_seen";
const ANIM_DONE_KEY = "libertify_ftu_anim_done";

const readFlag = (key: string) => {
  try {
    return !!localStorage.getItem(key);
  } catch {
    return false;
  }
};

export const OnboardingDashboard: React.FC<{ showWelcomeModal?: boolean }> = ({
  showWelcomeModal = false,
}) => {
  const navigate = useNavigate();

  // Server-side check: a user who already has a USE_CASE answer never sees
  // the questionnaire again, even after logout/re-login or on a new device.
  // Teacher orgs are excluded entirely — they have their own onboarding flow.
  const { shouldShowQuestionnaire, isLoading: isLoadingQuestionnaire } =
    useHasCompletedQuestionnaire();

  const welcomeSeen = readFlag(WELCOME_SEEN_KEY);

  const [modalOpen, setModalOpen] = useState(!welcomeSeen && showWelcomeModal);
  const [revealed, setRevealed] = useState(!modalOpen);
  const [animate] = useState(() => {
    try {
      return !sessionStorage.getItem(ANIM_DONE_KEY);
    } catch {
      return true;
    }
  });

  const revealContent = () => {
    try {
      localStorage.setItem(WELCOME_SEEN_KEY, "1");
    } catch {
      // Storage unavailable (e.g. private browsing)
    }
    setModalOpen(false);
    setRevealed(true);
    if (animate) {
      try {
        sessionStorage.setItem(ANIM_DONE_KEY, "1");
      } catch {
        // Storage unavailable (e.g. private browsing)
      }
    }
  };

  const handleStartCreating = () => {
    revealContent();
  };

  const handleCloseModal = () => {
    revealContent();
  };

  const handleSelectDocument = () => {
    navigate("/media/projects/document");
  };

  const handleSelectPrompt = () => {
    navigate("/media/projects/prompt");
  };

  if (isLoadingQuestionnaire) {
    return <LoadingSpinner />;
  }

  if (shouldShowQuestionnaire) {
    return <Navigate to="/questionnaire" replace />;
  }

  return (
    <>
      <WelcomeModal
        open={modalOpen}
        onStart={handleStartCreating}
        onClose={handleCloseModal}
      />
      <PageWrapper>
        <OnboardingHomeView
          revealed={revealed}
          animate={animate}
          onSelectDocument={handleSelectDocument}
          onSelectPrompt={handleSelectPrompt}
        />
      </PageWrapper>
    </>
  );
};
